/**
 * Handles lazy loading of Yotpo rating widgets
 * Lazy loads the ratings once widgets are in the viewport
 * @module yotpoLazyLoad
 */

import $ from 'jquery';
import registerJQueryPlugin from 'styleguide/lib/register-jquery-plugin';
import mergeOptions from 'styleguide/lib/merge-options';

export const PLUGIN_NAME = 'yotpoLazyLoad';
export const DEFAULTS = {};

/**
 * Loads yotpo ratings for a specific product element
 * @param {HTMLElement} productEl - The yotpo widget element
 */
export const loadYotpoRatings = (productEl) => {
  if (window.yotpoWidgetsContainer?.initWidget) {
    const instanceId = productEl.getAttribute('data-yotpo-instance-id') || productEl.dataset.yotpoInstanceId;
    if (instanceId) {
      window.yotpoWidgetsContainer.initWidget(instanceId, productEl);
      if (productEl.dataset.yotpoElementUninitialized === 'true') {
        productEl.classList.add('yotpo-widget-instance');
        productEl.removeAttribute('data-yotpo-element-uninitialized');
      }
    }
  } else {
    console.warn('Yotpo library not found or not ready. Check if script has loaded.');
  }
};

/**
 * Sets up the IntersectionObserver to lazy load yotpo widgets when in viewport
 * Falls back to initWidgets if IntersectionObserver is not supported
 */
export const setupYotpoObserver = () => {
  if ('IntersectionObserver' in window) {
    const options = {
      root: null,
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const productElement = entry.target;
          const { yotpoProductId: productId } = productElement.dataset;

          if (productId) {
            loadYotpoRatings(productElement);
          }
          // Prevent observation when user scrolls back up/down
          observer.unobserve(productElement);
        }
      });
    }, options);

    document.querySelectorAll('.yotpo-widget-instance:not([data-yotpo-element-loaded="true"])')
      .forEach(widget => {
        observer.observe(widget);
        widget.classList.remove('yotpo-widget-instance');
        widget.setAttribute('data-yotpo-element-uninitialized', 'true');
      });
  } else {
    // IntersectionObserver unsupported, use old code
    if (typeof yotpoWidgetsContainer !== 'undefined' && typeof yotpoWidgetsContainer.initWidgets !== 'undefined') {
      yotpoWidgetsContainer.initWidgets(false);
    }
  }
};

/**
 * Initializes yotpoLazyLoad.
 * @param {jQuery} el - targeting the element containing yotpo widgets
 * @param {Object} options - Options used for wiring up the yotpoLazyLoad
 * @see DEFAULTS
 */
export default function yotpoLazyLoad(el, options = {}) {
  const $el = $(el);
  const opts = mergeOptions(DEFAULTS, options, $el);

  setupYotpoObserver();
}

registerJQueryPlugin(PLUGIN_NAME, yotpoLazyLoad);


// WEBPACK FOOTER //
// ./src/scripts/entry-points/plp/yotpo-lazyload.js